"use client";

import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { LINE_FILTER_STRINGS } from "@/components/tools/line-filter";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import type { FilterResult } from "@/lib/tool-kit/qualitative-text/line-filter";

type Strings = typeof LINE_FILTER_STRINGS;

const PREVIEW_ROWS = 10;
const PREVIEW_CHARS = 80;

function clip(text: string): string {
  return text.length > PREVIEW_CHARS
    ? `${text.slice(0, PREVIEW_CHARS)}…`
    : text;
}

export function LineFilterErrors({
  result,
  strings,
}: {
  result: FilterResult;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <div className="space-y-2">
      {result.errors.map((error) => (
        <ErrorNotice key={`${error.term}-${error.source}`}>
          {t(
            error.problem === "slow" ? strings.slowTerm : strings.invalidTerm,
            {
              term: fmt(error.term),
              source: error.source,
              message: error.message,
            }
          )}
        </ErrorNotice>
      ))}
    </div>
  );
}

export function LineFilterResults({
  result,
  strings,
}: {
  result: FilterResult;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const shown = result.matched.slice(0, PREVIEW_ROWS);
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      {result.linesIn === 0 ? <Notice>{strings.noLines}</Notice> : null}
      {result.linesIn > 0 ? (
        <>
          <StatGrid>
            <StatCard label={strings.statLines} value={fmt(result.linesIn)} />
            <StatCard
              emphasis
              label={strings.statMatched}
              value={fmt(result.linesMatched)}
            />
            <StatCard
              label={strings.statMatches}
              value={fmt(result.totalMatches)}
            />
            <StatCard label={strings.statKept} value={fmt(result.linesKept)} />
            <StatCard
              label={strings.statRemoved}
              value={fmt(result.linesRemoved)}
            />
          </StatGrid>
          {result.linesMatched === 0 ? (
            <Notice>{strings.noMatches}</Notice>
          ) : null}
          <div className="space-y-2">
            <label className={LABEL} htmlFor="lf-output">
              {strings.outputLabel}
            </label>
            <textarea
              className={TEXTAREA}
              id="lf-output"
              readOnly
              rows={10}
              value={result.output}
            />
            <div className="flex flex-wrap gap-3">
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copy}
                text={result.output}
              />
              <button
                className={BUTTON_SECONDARY}
                onClick={() => downloadText(strings.filename, result.output)}
                type="button"
              >
                {strings.download}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.termsHeading}</h3>
            <DataTable
              head={[strings.colTerm, strings.colLines, strings.colMatches]}
              rows={result.terms.map((term) => [
                term.source,
                fmt(term.lines),
                fmt(term.matches),
              ])}
            />
          </div>
          {shown.length > 0 ? (
            <div className="space-y-2">
              <h3 className={WIDGET_H3}>
                {tPlural(strings.matchedHeading, locale, result.linesMatched, {
                  shown: fmt(shown.length),
                  total: fmt(result.linesMatched),
                })}
              </h3>
              <DataTable
                head={[strings.colLine, strings.colMatches, strings.colText]}
                rows={shown.map((row) => [
                  fmt(row.line),
                  fmt(row.matches),
                  clip(row.text),
                ])}
              />
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
